/**
 * 用户认证状态管理
 *
 * 提供用户登录状态、资料数据和认证相关操作
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, type UserProfile, type UpdateProfileInput } from '../api';
import { useToastStore } from './toast';

export const useUserStore = defineStore('user', () => {
  // State
  const isAuthenticated = ref(false);
  const user = ref<UserProfile | null>(null);
  const isLoading = ref(false);
  const showLoginPrompt = ref(false);

  // Toast
  const toast = useToastStore();

  // Computed
  const userEmail = computed(() => user.value?.email || '');
  const userNickname = computed(() => user.value?.nickname || user.value?.email || '');
  const userAvatar = computed(() => user.value?.avatar_url || '');

  /**
   * 初始化用户状态
   * 检查用户是否已登录，并加载用户资料
   */
  async function initialize() {
    try {
      const authState = await api.getAuthState();

      if (authState.authenticated && authState.user) {
        isAuthenticated.value = true;
        // 加载完整的用户资料
        await fetchUserProfile();
      } else {
        isAuthenticated.value = false;
        user.value = null;
      }
    } catch (error: any) {
      console.error('Failed to check auth status:', error);
      isAuthenticated.value = false;
      user.value = null;
    }
  }

  /**
   * 获取用户详细资料
   */
  async function fetchUserProfile() {
    try {
      isLoading.value = true;
      const response = await api.getCurrentUser();
      user.value = response.data;
    } catch (error: any) {
      console.error('Failed to fetch user profile:', error);
      if (error.code === 'UNAUTHORIZED') {
        handleUnauthorized();
      }
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * 更新用户资料
   */
  async function updateProfile(input: UpdateProfileInput) {
    try {
      isLoading.value = true;
      const response = await api.updateProfile(input);
      user.value = response.data;
      toast.success('资料已更新');
      return true;
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      toast.error(error.message || '更新失败');
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * 登出
   */
  async function logout() {
    try {
      const response = await api.logout();
      // 重定向到登出页面
      window.location.href = response.logoutUrl;
    } catch (error: any) {
      console.error('Failed to logout:', error);
      toast.error('登出失败');
    }
  }

  /**
   * 处理 401 未认证错误
   * 显示登录提示对话框
   */
  function handleUnauthorized() {
    isAuthenticated.value = false;
    user.value = null;
    showLoginPrompt.value = true;
  }

  /**
   * 关闭登录提示
   */
  function closeLoginPrompt() {
    showLoginPrompt.value = false;
  }

  /**
   * 跳转到登录页面
   */
  function goToLogin() {
    // 刷新页面，让 Cloudflare Access 重定向到登录页面
    window.location.reload();
  }

  return {
    // State
    isAuthenticated,
    user,
    isLoading,
    showLoginPrompt,

    // Computed
    userEmail,
    userNickname,
    userAvatar,

    // Actions
    initialize,
    fetchUserProfile,
    updateProfile,
    logout,
    handleUnauthorized,
    closeLoginPrompt,
    goToLogin,
  };
});
